import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

/**
 * SessionWarningModal
 *
 * Props:
 *   open           – boolean, whether the modal is visible
 *   secondsLeft    – number of seconds remaining until forced logout
 *   onStayLoggedIn – callback: user wants to extend the session
 *   onLogOut       – callback: user chooses to log out now
 *   onExpire       – callback: countdown reached zero → force logout
 */
export default function SessionWarningModal({
  open,
  secondsLeft,
  onStayLoggedIn,
  onLogOut,
  onExpire,
}) {
  const [displaySeconds, setDisplaySeconds] = useState(secondsLeft);

  // Sync display when the modal (re)opens with a fresh secondsLeft value
  useEffect(() => {
    if (!open) return;
    setDisplaySeconds(secondsLeft);
  }, [open, secondsLeft]);

  useEffect(() => {
    if (!open) return;
    if (displaySeconds <= 0) return;

    const tick = setInterval(() => {
      setDisplaySeconds((s) => {
        if (s <= 1) {
          clearInterval(tick);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(tick);
  }, [open]); // intentionally only re-run when modal opens

  // Force logout as soon as the local countdown hits zero
  useEffect(() => {
    if (open && displaySeconds === 0) {
      onExpire?.();
    }
  }, [displaySeconds, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const label =
    minutes > 0
      ? `${minutes}m ${String(seconds).padStart(2, "0")}s`
      : `${displaySeconds}s`;

  // Progress bar goes from 100 → 0 over the current warning window.
  // Pick the window size based on which threshold triggered this popup:
  // 10 min popup → 600 s window, 5 min popup → 300 s window, 1 min → 60 s window.
  const WARNING_WINDOWS = [600, 300, 60];
  const WARNING_WINDOW =
    WARNING_WINDOWS.find((w) => secondsLeft <= w) ?? WARNING_WINDOWS[0];
  const progress = Math.max(0, (displaySeconds / WARNING_WINDOW) * 100);

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1,
          fontFamily: "MAN EUROPE_Bold, sans-serif",
          fontSize: "1rem",
          color: "#cc0000",
        }}
      >
        <AccessTimeIcon sx={{ color: "#cc0000", fontSize: "1.3rem" }} />
        Session Expiring Soon
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: "#444", mb: 2, lineHeight: 1.6 }}
        >
          Your session will expire in{" "}
          <Box
            component="span"
            sx={{ fontWeight: 700, color: displaySeconds < 60 ? "#cc0000" : "#1e2832" }}
          >
            {label}
          </Box>
          . Please save any unsaved work, then choose to stay logged in or sign
          out.
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: "#f0f0f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: displaySeconds < 60 ? "#cc0000" : "#1e6b6b",
              borderRadius: 3,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onLogOut}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontFamily: "MAN EUROPE_Bold, sans-serif",
            fontSize: "0.8rem",
            borderColor: "#cc0000",
            color: "#cc0000",
            borderRadius: "8px",
            "&:hover": { borderColor: "#a00000", backgroundColor: "#fff5f5" },
          }}
        >
          Log Out Now
        </Button>
        <Button
          onClick={onStayLoggedIn}
          variant="contained"
          size="small"
          disabled={displaySeconds < 60}
          sx={{
            textTransform: "none",
            fontFamily: "MAN EUROPE_Bold, sans-serif",
            fontSize: "0.8rem",
            backgroundColor: "#1e6b6b",
            borderRadius: "8px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#155555", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#ccc", color: "#888" },
          }}
        >
          {displaySeconds < 60 ? "Session ending…" : "Stay Logged In"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
